import { atom, SetStateAction } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { atomWithDefault, atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';
import { isArray, isEqual, mapValues } from 'lodash';
import { SESSION_STATUS, SimInventoryFiltersV2 } from 'simInventory/models/SimProfile.model';
import {
  mapCustomFieldsToURL,
  mapURLSearchParamsToSimFilters,
} from 'src/common/components/Sim/Inventory/utils/filtersToURLSearcParamsMapper';
import {
  mapURLSearchParamsToSorting,
  Sorting,
  SortingParam,
} from 'src/common/components/Filters/constants/sorting';
import { Routes } from '../../Routes.constants';
import { createPaginationAtoms } from './utils/filtersFactory';
import { SIM_INVENTORY_ID_SELECTION } from 'src/common/components/Sim/constants/sim';
import { cleanDefaultValuesURLSearchParams } from 'src/common/utils/urlSearchParamsUtils';
import { SIM_FILTERS_INITIAL_VALUES_WITHOUT_CUSTOM_FIELDS } from './sim';
import { createSortingParamsSetter } from './utils/sorting';
import { isKeyOfObject } from 'src/common/utils/isKeyOfObject';

export const { rowsPerPageFilterAtom, pageFilterAtom } = createPaginationAtoms();

const ALLOWED_SORTING = new Set([
  'iccid',
  'imsi',
  'mnoStatus',
  'mnoStatusEntryTime',
  'msisdn',
  'liveDataUsage',
  'inMinuteUsage',
  'outMinuteUsage',
  'inTextUsage',
  'outTextUsage',
  'installerRef',
]);
const ALLOWED_SORTING_DIRECTION = new Set<Sorting>(['asc', 'desc']);
const setSimProductSortingParams = createSortingParamsSetter({
  allowedColumnNames: ALLOWED_SORTING,
  allowedDirection: ALLOWED_SORTING_DIRECTION,
});

export const SIM_PRODUCTS_FILTERS_INITIAL_VALUES_WITHOUT_CUSTOM_FIELDS: Omit<
  SimInventoryFiltersV2,
  'customFields'
> = {
  chosenIdType: SIM_INVENTORY_ID_SELECTION.ICCID,
  searchText: '',
  sessionStatus: SESSION_STATUS.UNKNOWN,
  accounts: [],
  productCodes: [],
  mobileNetworkOperator: [],
  mnoNames: [],
  connectionId: '',
  orderNumber: '',
  tags: [],
  iccidFrom: '',
  iccidTo: '',
  mnoStatus: [],
  subscriptionStatus: [],
  mobileNetworkOperatorId: [],
};

export const SIM_PRODUCTS_FILTERS_INITIAL_VALUES: SimInventoryFiltersV2 = {
  ...SIM_PRODUCTS_FILTERS_INITIAL_VALUES_WITHOUT_CUSTOM_FIELDS,
  customFields: {},
};

const filtersAtom = atomWithDefault<SimInventoryFiltersV2>(() => {
  if (globalThis.location.pathname === Routes.productsSim) {
    const params = new URLSearchParams(globalThis.location.search);
    return mapURLSearchParamsToSimFilters(params);
  }

  return SIM_PRODUCTS_FILTERS_INITIAL_VALUES;
});

export const simProductsFiltersAtom = atom(
  (get) => get(filtersAtom),
  (get, set, filters: SetStateAction<SimInventoryFiltersV2> | typeof RESET) => {
    const currentSimProductsFilters = get(filtersAtom);

    if (!isEqual(currentSimProductsFilters, filters)) {
      set(pageFilterAtom, 0);
    }

    if (filters === RESET) {
      const currentFilters = get(filtersAtom);
      const customFields = mapValues(currentFilters.customFields, (customFieldValue) => {
        if (Array.isArray(customFieldValue)) {
          return [];
        }

        return '';
      });

      return set(filtersAtom, { ...SIM_PRODUCTS_FILTERS_INITIAL_VALUES, customFields });
    }

    return set(filtersAtom, filters);
  },
);

export const simProdcutsSubscriptionStatusFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('subscriptionStatus'),
);

export const sessionStatusFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('sessionStatus'),
);

export const accountsFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('accounts'),
);

export const productCodesFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('productCodes'),
);

export const mobileNetworkOperatorFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('mobileNetworkOperator'),
);

export const simProductsMobileNetworkOperatorIdFilterAtom = focusAtom(
  simProductsFiltersAtom,
  (optic) => optic.prop('mobileNetworkOperatorId'),
);

export const mnoNamesFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('mnoNames'),
);

export const connectionIdFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('connectionId'),
);

export const orderNumberFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('orderNumber'),
);

export const tagsFilterAtom = focusAtom(simProductsFiltersAtom, (optic) => optic.prop('tags'));

export const iccidRangeFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.pick(['iccidFrom', 'iccidTo']),
);

export const chosenIdTypeFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('chosenIdType'),
);

export const customFieldsFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('customFields'),
);

export const searchTextFilterAtom = focusAtom(simProductsFiltersAtom, (optic) =>
  optic.prop('searchText'),
);

export const hasActiveFilterAtom = atom((get) => {
  const filters = get(simProductsFiltersAtom);

  return Object.entries(filters).some(([key, value]) => {
    if (!isKeyOfObject(key, SIM_PRODUCTS_FILTERS_INITIAL_VALUES)) {
      return false;
    }

    if (key === 'chosenIdType' || key === 'searchText') {
      return false;
    }

    if (isArray(value) && isEqual(value, [])) {
      return false;
    }

    if (key === 'customFields') {
      const customField = Object.values(value);
      return customField.some((value) => value !== '' && !isEqual(value, []));
    }

    return value !== SIM_PRODUCTS_FILTERS_INITIAL_VALUES[key];
  });
});

const atomLocalStorage = createJSONStorage<SortingParam>(() => localStorage);

const STORAGE_VERSION = 'v1';
const STORAGE_NAME = `sim-products-order`;
const STORAGE_KEY = `${STORAGE_VERSION}-${STORAGE_NAME}`;

export const simProductsSortingAtomWithStorage = atomWithStorage<SortingParam>(
  STORAGE_KEY,
  {
    columnName: 'iccid',
    sort: 'desc',
  } as SortingParam,
  atomLocalStorage,
  { getOnInit: true },
);

export const simProductsUrlParamsAtom = atom(
  (get) => {
    const { customFields, sessionStatus, ...othersFilters } = get(simProductsFiltersAtom);
    const cleanedFiltersValues = cleanDefaultValuesURLSearchParams(
      { ...othersFilters, sessionStatus: sessionStatus.toString() },
      {
        ...SIM_FILTERS_INITIAL_VALUES_WITHOUT_CUSTOM_FIELDS,
        sessionStatus: SIM_FILTERS_INITIAL_VALUES_WITHOUT_CUSTOM_FIELDS.sessionStatus.toString(),
      },
    );

    const searchParams = new URLSearchParams({
      ...cleanedFiltersValues,
    });

    const stringifiedCustomFields = mapCustomFieldsToURL(customFields);
    if (stringifiedCustomFields !== '') {
      searchParams.append('customFields', stringifiedCustomFields);
    }

    const sorting = get(simProductsSortingAtomWithStorage);
    setSimProductSortingParams({ searchParams, sorting });

    return searchParams;
  },
  (_, set, urlSearchParams: URLSearchParams) => {
    const filters = mapURLSearchParamsToSimFilters(urlSearchParams);
    set(simProductsFiltersAtom, filters);

    const sort = mapURLSearchParamsToSorting(urlSearchParams);
    if (sort) {
      set(simProductsSortingAtomWithStorage, sort);
    }
  },
);
